import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Badge,
  ProgressBar,
  Tab,
  Nav,
  Table,
  ListGroup,
  Accordion
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRedo,
  faPrint,
  faChartBar,
  faClipboardList,
  faExclamationTriangle,
  faTools,
  faShoppingCart,
  faUsers,
  faChartLine,
  faInfoCircle,
  faCheck,
  faCalendarAlt,
  faHeart,
  faClock,
  faDollarSign,
  faHeartbeat,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

const Results = ({ data, onReset, fromSaved = false, onBack }) => {
  const [activeTab, setActiveTab] = useState('summary');
  
  // Function to print the maintenance plan
  const handlePrintPlan = () => {
    const planHTML = generatePrintableHTML();
    
    // Create a new window with a meaningful title and URL
    const newWindow = window.open("", "_blank");

    if (newWindow) {
      newWindow.document.title = `Maintenance Plan - ${data.venue_summary?.name || 'Venue'} - ${new Date().toLocaleDateString()}`;

      newWindow.document.open();
      newWindow.document.write(planHTML);
      newWindow.document.close();

      // Automatically trigger print when the page is loaded
      newWindow.addEventListener("load", () => {
        setTimeout(() => {
          newWindow.print();
        }, 500); // Small delay to ensure styles are applied
      });
    } else {
      alert("Popup blocked. Please allow popups for this site to view the printable report.");
    }
  };

  // Generate HTML content for printing
  const generatePrintableHTML = () => {
    const venueSummary = data.venue_summary || {};
    const summary = data.summary || {};
    const scores = data.venue_health_scores || {};
    const systemScores = scores.system_scores || {};
    
    // Create a progress bar HTML
    const createProgressBar = (value, label = '') => {
      let colorClass = 'success';
      if (value < 50) colorClass = 'danger';
      else if (value < 75) colorClass = 'warning';
      
      return `
        <div class="progress-container">
          <div class="progress-bar ${colorClass}" style="width: ${value}%">${label || value + '%'}</div>
        </div>
      `;
    };
    
    // Generate HTML sections
    const summaryHTML = generateSummaryHTML();
    const tasksHTML = generateTasksHTML();
    const risksHTML = generateRisksHTML();
    const equipmentHTML = generateEquipmentHTML();
    const recommendationsHTML = generateRecommendationsHTML();
    const sparePartsHTML = generateSparePartsHTML();
    const staffingHTML = generateStaffingHTML();
    const healthScoresHTML = generateHealthScoresHTML();
    
    // Generate summary section
    function generateSummaryHTML() {
      return `
        <section>
          <h2>Venue Summary</h2>
          <div class="card">
            <div style="display: flex; flex-wrap: wrap;">
              <div style="flex: 1; min-width: 300px;">
                <h3>Venue Overview</h3>
                <table class="info-table">
                  <tr><td><strong>Name:</strong></td><td>${venueSummary.name || 'N/A'}</td></tr>
                  <tr><td><strong>Type:</strong></td><td>${venueSummary.type || 'N/A'}</td></tr>
                  <tr><td><strong>Age:</strong></td><td>${venueSummary.age || 'N/A'} years</td></tr>
                  <tr><td><strong>Overall Condition:</strong></td><td>${venueSummary.overall_condition || 'N/A'}</td></tr>
                </table>
              </div>
              <div style="flex: 1; min-width: 300px;">
                <h3>Health Scores</h3>
                <p><strong>Overall Health:</strong> ${scores.overall_score || 0}/100</p>
                ${createProgressBar(scores.overall_score || 0)}
                <p><strong>Reliability:</strong> ${scores.reliability_score || 0}/100</p>
                ${createProgressBar(scores.reliability_score || 0)}
                <p><strong>Event Readiness:</strong> ${scores.readiness_for_upcoming_events || 0}/100</p>
                ${createProgressBar(scores.readiness_for_upcoming_events || 0)}
              </div>
            </div>
            
            <h3 style="margin-top: 20px;">Maintenance Summary</h3>
            <div class="summary-metrics">
              <div class="summary-metric critical">
                <span class="value">${summary.critical_issues_count || 0}</span>
                <span class="label">Critical Issues</span>
              </div>
              <div class="summary-metric high">
                <span class="value">${summary.high_priority_tasks_count || 0}</span>
                <span class="label">High Priority</span>
              </div>
              <div class="summary-metric medium">
                <span class="value">${summary.medium_priority_tasks_count || 0}</span>
                <span class="label">Medium Priority</span>
              </div>
              <div class="summary-metric low">
                <span class="value">${summary.low_priority_tasks_count || 0}</span>
                <span class="label">Low Priority</span>
              </div>
            </div>
            
            <div style="display: flex; flex-wrap: wrap; margin-top: 15px;">
              <div style="flex: 1; min-width: 300px;">
                <table class="info-table">
                  <tr>
                    <td><strong>Total Maintenance Hours:</strong></td>
                    <td>${summary.estimated_total_maintenance_hours || 0} hours</td>
                  </tr>
                  <tr>
                    <td><strong>Estimated Cost:</strong></td>
                    <td>${summary.estimated_total_maintenance_cost || '$0'}</td>
                  </tr>
                </table>
              </div>
              <div style="flex: 1; min-width: 300px;">
                <h4>Main Concerns</h4>
                <ul>
                  ${summary.main_concerns?.map(concern => `<li>${concern}</li>`).join('') || '<li>No main concerns identified</li>'}
                </ul>
              </div>
            </div>
            
            <div class="strengths-section">
              <h4>Strengths</h4>
              <div class="strengths-container">
                ${summary.strengths?.map(strength => 
                  `<span class="strength-tag">${strength}</span>`
                ).join('') || 'No specific strengths identified'}
              </div>
            </div>
          </div>
        </section>
      `;
    }

    // Generate tasks section
    function generateTasksHTML() {
      if (!data.maintenance_tasks?.length) return '';
      
      // Determine if this is a small dataset that should be kept together
      const smallDataset = data.maintenance_tasks.length <= 3;
      const datasetClass = smallDataset ? 'small-dataset' : '';
      
      return `
        <section>
          <h2>Maintenance Tasks</h2>
          <div class="${datasetClass}">
            ${data.maintenance_tasks.map((task, index) => `
              <div class="task-card">
                <div class="task-header">
                  <span class="badge ${task.criticality.toLowerCase()}">${task.criticality}</span>
                  <strong>${task.system}</strong> - ${task.component} (${task.timing})
                </div>
                <div class="task-body">
                  <p>${task.task_description}</p>
                  <p><strong>Justification:</strong> ${task.justification}</p>
                  <p><strong>Technical Details:</strong> ${task.technical_justification}</p>
                  
                  <div class="two-column">
                    <div class="column">
                      <h4>Resources</h4>
                      <ul>
                        <li><strong>Estimated Time:</strong> ${task.estimated_time}</li>
                        <li><strong>Impact if Skipped:</strong> ${task.impact_if_not_addressed}</li>
                      </ul>
                      ${task.resources_needed?.length ? `
                        <h4>Required Resources</h4>
                        <ul>
                          ${task.resources_needed.map(resource => `<li>${resource}</li>`).join('')}
                        </ul>
                      ` : ''}
                    </div>
                    <div class="column">
                      <h4>Related Equipment</h4>
                      ${task.related_equipment?.length ? `
                        <ul>
                          ${task.related_equipment.map(equipment => `<li>${equipment}</li>`).join('')}
                        </ul>
                      ` : '<p>No related equipment specified</p>'}
                    </div>
                  </div>
                </div>
              </div>
            `).join('')}
          </div>
        </section>
      `;
    }

    // Generate risks section
    function generateRisksHTML() {
      if (!data.event_risk_assessment?.length) return '';
      
      const smallDataset = data.event_risk_assessment.length <= 2;
      const datasetClass = smallDataset ? 'small-dataset' : '';
      
      return `
        <section>
          <h2>Event Risk Assessment</h2>
          <div class="${datasetClass}">
            ${data.event_risk_assessment.map((event, index) => `
              <div class="event-card">
                <div class="event-header">
                  <span class="badge ${event.overall_risk_level.toLowerCase()}">${event.overall_risk_level}</span>
                  <strong>${event.event_name}</strong> - ${event.event_date}
                </div>
                <div class="event-body">
                  <p><strong>Event Type:</strong> ${event.event_type} | <strong>Technical Complexity:</strong> ${event.technical_complexity}/5</p>
                  <p><strong>Expected Attendance:</strong> ${event.expected_attendance.toLocaleString()}</p>
                  
                  <h4>Risk Areas</h4>
                  ${event.risk_areas?.map(risk => `
                    <div class="risk-box">
                      <div class="risk-header">
                        <div>
                          <h5>${risk.system} Risk</h5>
                          <p>${risk.risk_description}</p>
                        </div>
                        <div class="risk-metrics">
                          <span class="badge">Severity: ${risk.severity}</span>
                          <span class="badge">Probability: ${risk.probability}</span>
                        </div>
                      </div>
                      
                      <div class="two-column">
                        <div class="column">
                          <h5>Mitigation Steps</h5>
                          <ul>
                            ${risk.mitigation_steps?.map(step => `<li>${step}</li>`).join('') || '<li>No mitigation steps defined</li>'}
                          </ul>
                        </div>
                        <div class="column">
                          <h5>Related Past Issues</h5>
                          <ul>
                            ${risk.related_past_issues?.length > 0 ? risk.related_past_issues.map(issue => `<li>${issue}</li>`).join('') : '<li>No past issues recorded</li>'}
                          </ul>
                        </div>
                      </div>
                      
                      <div class="contingency-section">
                        <h5>Contingency Plan</h5>
                        <p>${risk.contingency_plan}</p>
                      </div>
                    </div>
                  `).join('')}
                </div>
              </div>
            `).join('')}
          </div>
        </section>
      `;
    }

    // Generate equipment section
    function generateEquipmentHTML() {
      if (!data.equipment_health_assessment?.length) return '';
      
      return `
        <section>
          <h2>Equipment Health Assessment</h2>
          ${data.equipment_health_assessment.map((system, index) => `
            <div class="system-card">
              <div class="system-header">
                <strong>${system.system_category} System</strong>
                <span>(${system.components?.length || 0} components)</span>
              </div>
              <div class="system-body">
                ${system.components?.map(component => `
                  <div class="component-box">
                    <div class="component-header">
                      <div>
                        <h5>${component.name}</h5>
                        <p>${component.make_model} | Age: ${component.age} years</p>
                      </div>
                      <span class="badge ${component.maintenance_urgency.toLowerCase()}">${component.maintenance_urgency} Urgency</span>
                    </div>
                    
                    <div class="condition-meter">
                      <p>Condition Rating: ${component.condition_rating}/5</p>
                      ${createProgressBar(component.condition_rating * 20)}
                    </div>
                    
                    <div class="two-column">
                      <div class="column">
                        <p><strong>Failure Probability:</strong> ${(component.failure_probability * 100).toFixed(1)}%</p>
                        <p><strong>Remaining Life:</strong> ${component.estimated_remaining_life}</p>
                        <p><strong>Replacement Cost:</strong> ${component.replacement_cost}</p>
                      </div>
                      <div class="column">
                        <h5>Warning Signs</h5>
                        ${component.warning_signs?.length ? `
                          <ul class="warning-list">
                            ${component.warning_signs.map(sign => `<li>${sign}</li>`).join('')}
                          </ul>
                        ` : '<p>No warning signs detected</p>'}
                      </div>
                    </div>
                    
                    <p class="component-notes"><strong>Notes:</strong> ${component.notes}</p>
                  </div>
                `).join('') || '<p>No components defined</p>'}
              </div>
            </div>
          `).join('')}
        </section>
      `;
    }

    // Generate recommendations section
    function generateRecommendationsHTML() {
      if (!data.long_term_recommendations?.length) return '';
      
      return `
        <section>
          <h2>Long-Term Recommendations</h2>
          ${data.long_term_recommendations.map((rec, index) => `
            <div class="recommendation-card">
              <div class="recommendation-header">
                <span class="badge ${rec.priority.toLowerCase()}">${rec.priority}</span>
                <strong>${rec.category}</strong> - ${rec.recommendation}
              </div>
              <div class="recommendation-body">
                <p class="justification">${rec.justification}</p>
                
                <div class="two-column">
                  <div class="column">
                    <table class="info-table">
                      <tr>
                        <td><strong>Estimated Cost:</strong></td>
                        <td>${rec.estimated_cost}</td>
                      </tr>
                      <tr>
                        <td><strong>Implementation Timeline:</strong></td>
                        <td>${rec.implementation_timeline}</td>
                      </tr>
                      <tr>
                        <td><strong>ROI Estimate:</strong></td>
                        <td>${rec.roi_estimate}</td>
                      </tr>
                    </table>
                    
                    <h5>Affected Systems</h5>
                    <ul>
                      ${rec.affected_systems?.map(sys => `<li>${sys}</li>`).join('') || '<li>No affected systems specified</li>'}
                    </ul>
                  </div>
                  <div class="column">
                    <h5>Expected Benefits</h5>
                    <ul>
                      ${rec.expected_benefits?.map(benefit => `<li>${benefit}</li>`).join('') || '<li>No expected benefits specified</li>'}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          `).join('')}
        </section>
      `;
    }

    // Generate spare parts section with table
    function generateSparePartsHTML() {
      if (!data.spare_parts_recommendations?.length) return '';
      
      const smallDataset = data.spare_parts_recommendations.length <= 5;
      const datasetClass = smallDataset ? 'small-dataset' : 'large-dataset';
      
      return `
        <section>
          <h2>Spare Parts Recommendations</h2>
          <table class="data-table ${datasetClass}">
            <thead>
              <tr>
                <th>Part Name</th>
                <th>Qty</th>
                <th>Criticality</th>
                <th>Est. Cost</th>
                <th>Lead Time</th>
                <th>Compatible With</th>
              </tr>
            </thead>
            <tbody>
              ${data.spare_parts_recommendations.map(part => `
                <tr>
                  <td>
                    <strong>${part.part_name}</strong>
                    <div class="text-muted">${part.justification}</div>
                  </td>
                  <td>${part.quantity_recommended}</td>
                  <td><span class="badge ${part.criticality.toLowerCase()}">${part.criticality}</span></td>
                  <td>${part.estimated_cost}</td>
                  <td>${part.lead_time}</td>
                  <td>
                    <ul>
                      ${part.compatible_equipment?.map(item => `<li>${item}</li>`).join('') || '<li>No compatible equipment specified</li>'}
                    </ul>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </section>
      `;
    }

    // Generate staffing section with table
    function generateStaffingHTML() {
      if (!data.staffing_recommendations?.length) return '';
      
      const smallDataset = data.staffing_recommendations.length <= 5;
      const datasetClass = smallDataset ? 'small-dataset' : 'large-dataset';
      
      return `
        <section>
          <h2>Staffing Recommendations</h2>
          <table class="data-table ${datasetClass}">
            <thead>
              <tr>
                <th>Event Type</th>
                <th>Role</th>
                <th>Qty</th>
                <th>Expertise</th>
                <th>Coverage</th>
                <th>Cost Est.</th>
              </tr>
            </thead>
            <tbody>
              ${data.staffing_recommendations.map(staff => `
                <tr>
                  <td>${staff.event_type}</td>
                  <td>
                    <strong>${staff.role}</strong>
                    <div class="text-muted">${staff.justification}</div>
                  </td>
                  <td>${staff.quantity_needed}</td>
                  <td>
                    <span class="badge ${staff.expertise_level.toLowerCase()}">${staff.expertise_level}</span>
                    <div>
                      ${staff.specialized_skills?.slice(0, 2).map(skill => `<div class="text-muted">${skill}</div>`).join('')}
                      ${staff.specialized_skills?.length > 2 ? `<div class="text-muted">+${staff.specialized_skills.length - 2} more</div>` : ''}
                    </div>
                  </td>
                  <td>${staff.ideal_coverage_hours}</td>
                  <td>${staff.cost_estimate}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </section>
      `;
    }

    // Generate health scores section
    function generateHealthScoresHTML() {
      if (!scores) return '';
      
      return `
        <section>
          <h2>Venue Health Scores</h2>
          <div class="health-score-card">
            <div class="overall-score">
              <h1>${scores.overall_score || '—'}</h1>
              <p>Overall Health Score</p>
            </div>
            
            ${createProgressBar(scores.overall_score || 0)}
            
            <div class="score-metrics">
              <div class="score-metric">
                <h3>${scores.reliability_score || '—'}</h3>
                <div>Reliability</div>
              </div>
              <div class="score-metric">
                <h3>${scores.safety_score || '—'}</h3>
                <div>Safety</div>
              </div>
              <div class="score-metric">
                <h3>${scores.readiness_for_upcoming_events || '—'}</h3>
                <div>Event Readiness</div>
              </div>
            </div>
          </div>
          
          <h3>System Health Scores</h3>
          <div class="system-scores">
            ${Object.entries(systemScores).map(([system, score]) => `
              <div class="system-score">
                <div class="system-score-header">
                  <div class="system-name">${system}</div>
                  <div class="system-value">${score}/100</div>
                </div>
                ${createProgressBar(score)}
              </div>
            `).join('')}
          </div>
        </section>
      `;
    }
    
    // Create the logo HTML with BeatAI logo - left aligned
    const logoHTML = `
      <div class="logo-container">
        <img src="https://beatai.io/assets/img/beatai-logo.svg" alt="BeatAI Logo" class="logo">
        <h1>Predictive Maintenance Plan</h1>
        <p>Generated for ${venueSummary.name || 'your venue'} on ${new Date().toLocaleDateString()}</p>
      </div>
    `;
    
    // CSS for print styling with purple shades for headings
    const printStyles = `
      <style type="text/css">
        @media print {
          @page {
            size: auto;
            margin: 10mm 8mm 10mm 8mm;
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.2;
            color: #333;
            background: #fff;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 0.8rem;
          }
          .logo-container {
            text-align: left;
            margin-bottom: 10px;
            padding: 8px 5px;
          }
          .logo {
            max-height: 35px;
            margin-bottom: 5px;
          }
          h1 { 
            color: #6e2ae8; 
            border-bottom: 1px solid #6e2ae8;
            padding-bottom: 3px;
            text-align: center;
            margin: 10px 0 8px;
            font-size: 1.4rem;
          }
          h2 { 
            color: #6e2ae8;
            margin-top: 12px;
            margin-bottom: 8px;
            padding: 4px;
            background: #f5f0ff;
            border-left: 3px solid #6e2ae8;
            font-size: 1.1rem;
          }
          h3 { 
            color: #6e2ae8;
            margin-top: 10px;
            margin-bottom: 4px;
            font-size: 1rem;
          }
          h4 {
            color: #4a148c;
            margin-top: 8px;
            margin-bottom: 4px;
            font-size: 0.9rem;
          }
          
          /* Allow content to flow naturally between pages */
          section {
            margin-bottom: 12px;
            page-break-before: auto;
            page-break-after: auto;
          }
          
          /* Remove forced page breaks between sections that aren't needed */
          .page-break {
            page-break-after: auto;
          }
          
          /* Only force page breaks before major sections when needed */
          section.major-section {
            page-break-before: always;
          }
          
          /* Smart page break control */
          .task-card, .event-card, .system-card, .recommendation-card {
            page-break-inside: avoid;
            break-inside: avoid;
            margin-bottom: 8px;
            padding: 6px;
            border: 1px solid #ddd;
            background: #fff;
            border-radius: 3px;
          }
          
          /* Allow child items to flow between pages when parent container is large */
          .component-box, .risk-box {
            margin-bottom: 6px;
            padding: 6px;
            border: 1px solid #ddd;
            background: #fff;
            border-radius: 3px;
          }
          
          /* For small datasets, keep components together */
          .small-dataset .component-box,
          .small-dataset .risk-box {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          .task-header, .event-header, .system-header, .recommendation-header {
            border-bottom: 1px solid #eee;
            padding-bottom: 4px;
            margin-bottom: 4px;
          }
          
          .component-header, .risk-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
          }
          
          .contingency-section {
            margin-top: 6px;
            padding-top: 4px;
            border-top: 1px solid #eee;
          }
          
          .component-notes {
            margin-top: 4px;
            padding-top: 4px;
            border-top: 1px solid #eee;
          }
          
          .data-table { 
            width: 100%;
            border-collapse: collapse; 
            margin: 8px 0;
            box-shadow: none;
            font-size: 0.75rem;
          }
          
          .data-table, .data-table th, .data-table td { 
            border: 1px solid #ddd; 
          }
          
          .data-table th { 
            background-color: #6e2ae8 !important;
            color: white !important;
            padding: 4px 6px;
            text-align: left;
          }
          
          .data-table td {
            padding: 3px 5px;
            text-align: left;
          }
          
          /* Increase density for small datasets */
          table.small-dataset tr { 
            page-break-inside: avoid; 
            break-inside: avoid;
          }
          
          /* Allow row breaks for large datasets */
          table.large-dataset tr { 
            page-break-inside: auto; 
            break-inside: auto;
          }
          
          .info-table {
            width: 100%;
            border-collapse: collapse;
            margin: 6px 0;
          }
          
          .info-table, .info-table th, .info-table td {
            border: none; 
            border-bottom: 1px solid #eee;
          }
          
          .info-table td {
            padding: 3px 2px;
          }
          
          .info-table td:first-child {
            width: 40%;
          }
          
          .two-column {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin: 6px 0;
          }
          
          .column {
            flex: 1;
            min-width: 220px;
          }
          
          .badge {
            background: #6e2ae8;
            color: white;
            padding: 1px 4px;
            border-radius: 2px;
            font-size: 0.65rem;
            font-weight: 500;
          }
          
          .progress-container {
            background: #f2f2f2;
            border-radius: 2px;
            margin: 2px 0;
            height: 10px;
            overflow: hidden;
          }
          
          .progress-bar {
            height: 100%;
            color: white;
            text-align: center;
            font-size: 0.65rem;
            line-height: 10px;
          }
          
          .warning-list {
            color: #dc3545;
            margin: 3px 0;
            padding-left: 15px;
          }
          
          .justification {
            font-style: italic;
            color: #666;
            margin-bottom: 6px;
          }
          
          p {
            margin: 2px 0 4px;
          }
          
          ul {
            margin: 3px 0;
            padding-left: 18px;
          }
          
          li {
            margin-bottom: 2px;
          }
          
          .summary-metrics {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
            margin: 8px 0;
          }
          
          .summary-metric {
            background: #f9f6ff;
            padding: 6px;
            border-radius: 3px;
            text-align: center;
            border: 1px solid #e7e0ff;
          }
          
          .summary-metric .value {
            display: block;
            font-size: 1rem;
            color: #6e2ae8;
            font-weight: bold;
          }
          
          .summary-metric .label {
            display: block;
            color: #666;
            font-size: 0.7rem;
            margin-top: 1px;
          }
          
          .strengths-container {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
            margin-top: 4px;
          }
          
          .strength-tag {
            background: #6e2ae8;
            color: white;
            padding: 2px 6px;
            border-radius: 12px;
            font-size: 0.7rem;
          }
          
          .strengths-section {
            margin-top: 8px;
          }
          
          .health-score-card {
            background: #f9f6ff;
            border: 1px solid #e7e0ff;
            border-radius: 3px;
            padding: 8px;
            margin-bottom: 8px;
          }
          
          .overall-score {
            text-align: center;
            margin-bottom: 8px;
          }
          
          .overall-score h1 {
            font-size: 1.8rem;
            margin: 0;
            padding: 0;
            border: none;
          }
          
          .score-metrics {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 4px;
            margin-top: 8px;
          }
          
          .score-metric {
            text-align: center;
            background: white;
            padding: 4px;
            border-radius: 3px;
          }
          
          .score-metric h3 {
            margin: 0;
            color: #6e2ae8;
            margin-bottom: 1px;
          }
          
          .system-scores {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
            margin-top: 8px;
          }
          
          .system-score {
            background: #f9f6ff;
            border: 1px solid #e7e0ff;
            border-radius: 3px;
            padding: 4px;
          }
          
          .system-score-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
          }
          
          .text-muted {
            color: #666;
            font-size: 0.7rem;
          }
          
          /* Adaptive styling for data density */
          @media print and (max-height: 500px) {
            body { line-height: 1.1; }
            p { margin: 1px 0 3px; }
            ul { margin: 2px 0; }
            li { margin-bottom: 1px; }
            .task-card, .event-card, .system-card, .recommendation-card { padding: 4px; margin-bottom: 6px; }
            .component-box, .risk-box { padding: 4px; margin-bottom: 4px; }
          }
        }
      </style>
    `;
    
    // Combine all sections into the final HTML document
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Maintenance Plan</title>
        ${printStyles}
      </head>
      <body>
        ${logoHTML}
        ${summaryHTML}
        ${tasksHTML ? tasksHTML : ''}
        ${risksHTML ? risksHTML : ''}
        ${equipmentHTML ? equipmentHTML : ''}
        ${recommendationsHTML ? recommendationsHTML : ''}
        ${sparePartsHTML ? sparePartsHTML : ''}
        ${staffingHTML ? staffingHTML : ''}
        ${healthScoresHTML ? healthScoresHTML : ''}
      </body>
      </html>
    `;
  };

  // Helper function to determine badge color based on criticality/priority
  const getCriticalityBadgeColor = (criticality) => {
    // Return style object instead of variant name
    return "linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)";
  };
  
  // Helper function to render progress bar with color based on score
  const renderScoreProgressBar = (score) => {
    let variant = 'success';
    if (score < 50) variant = 'danger';
    else if (score < 75) variant = 'warning';
    
    return (
      <ProgressBar 
        now={score} 
        variant={variant}
        label={`${score}%`} 
        style={{ height: '12px', fontSize: '9px', fontWeight: '600' }} 
      />
    );
  };
  
  // Maintenance Tasks Tab content
  const renderMaintenanceTasks = () => {
    return (
      <div className="mt-3">
        <h5 className="text-white mb-3">Maintenance Tasks ({data.maintenance_tasks?.length || 0})</h5>
        <Accordion defaultActiveKey="0">
          {data.maintenance_tasks?.map((task, index) => (
            <Accordion.Item 
              key={task.task_id} 
              eventKey={index.toString()}
              className="mb-3"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px'
              }}
            >
              <Accordion.Header style={{ color: '#ffffff' }}>
                <div className="d-flex justify-content-between w-100 align-items-center pe-3">
                  <div>
                    <Badge style={{ background: getCriticalityBadgeColor(task.criticality) }} className="me-2">{task.criticality}</Badge>
                    <strong>{task.system}</strong> - {task.component}
                  </div>
                  <div className="small text-muted">{task.timing}</div>
                </div>
              </Accordion.Header>
              <Accordion.Body style={{ background: 'rgba(0, 0, 0, 0.2)' }}>
                <Row>
                  <Col md={12}>
                    <p style={{ color: '#d9d9d9', marginBottom: '10px' }}>{task.task_description}</p>
                    <p className="small text-muted mb-2"><strong>Justification:</strong> {task.justification}</p>
                    <p className="small text-muted mb-2"><strong>Technical Details:</strong> {task.technical_justification}</p>
                  </Col>
                  
                  <Col md={6} className="mt-3">
                    <h6 style={{ color: '#d9d9d9', fontSize: '0.9rem' }}>Resources</h6>
                    <ul className="small" style={{ color: '#adb5bd' }}>
                      <li><strong>Estimated Time:</strong> {task.estimated_time}</li>
                      <li><strong>Impact if Skipped:</strong> {task.impact_if_not_addressed}</li>
                    </ul>
                    {task.resources_needed?.length > 0 && (
                      <>
                        <h6 style={{ color: '#d9d9d9', fontSize: '0.9rem', marginTop: '10px' }}>Required Resources</h6>
                        <ul className="small" style={{ color: '#adb5bd' }}>
                          {task.resources_needed.map((resource, i) => (
                            <li key={i}>{resource}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Col>
                  
                  <Col md={6} className="mt-3">
                    <h6 style={{ color: '#d9d9d9', fontSize: '0.9rem' }}>Related Equipment</h6>
                    {task.related_equipment?.length > 0 ? (
                      <ul className="small" style={{ color: '#adb5bd' }}>
                        {task.related_equipment.map((equipment, i) => (
                          <li key={i}>{equipment}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="small text-muted">No related equipment specified</p>
                    )}
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };
  
  // Event Risk Assessment Tab
  const renderRiskAssessment = () => {
    return (
      <div className="mt-3">
        <h5 className="text-white mb-3">Event Risk Assessment ({data.event_risk_assessment?.length || 0})</h5>
        <Accordion>
          {data.event_risk_assessment?.map((event, eventIndex) => (
            <Accordion.Item 
              key={eventIndex} 
              eventKey={eventIndex.toString()}
              className="mb-3"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px'
              }}
            >
              <Accordion.Header style={{ color: '#ffffff' }}>
                <div className="d-flex justify-content-between w-100 align-items-center pe-3">
                  <div>
                    <Badge 
                      style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }} 
                      className="me-2"
                    >
                      {event.overall_risk_level}
                    </Badge>
                    <strong>{event.event_name}</strong> - {event.event_date}
                  </div>
                  <div className="small text-muted">Attendance: {event.expected_attendance.toLocaleString()}</div>
                </div>
              </Accordion.Header>
              <Accordion.Body style={{ background: 'rgba(0, 0, 0, 0.2)' }}>
                <p className="text-white small mb-3">
                  <strong>Event Type:</strong> {event.event_type} | 
                  <strong> Technical Complexity:</strong> {event.technical_complexity}/5
                </p>
                
                <h6 className="text-white" style={{ fontSize: '0.9rem' }}>Risk Areas</h6>
                <div className="mt-2">
                  {event.risk_areas?.map((risk, riskIndex) => (
                    <Card 
                      key={riskIndex}
                      className="mb-3" 
                      style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 style={{ color: '#d9d9d9' }}>{risk.system} Risk</h6>
                            <p className="small text-muted mb-2">{risk.risk_description}</p>
                          </div>
                          <div>
                            <Badge style={{ background: getCriticalityBadgeColor(risk.severity) }} className="me-1">
                              Severity: {risk.severity}
                            </Badge>
                            <Badge style={{ background: 'rgba(77, 122, 255, 0.8)' }}>
                              Probability: {risk.probability}
                            </Badge>
                          </div>
                        </div>
                        
                        <Row className="mt-3">
                          <Col md={6}>
                            <h6 className="small text-white mb-2">Mitigation Steps</h6>
                            <ul className="small" style={{ color: '#adb5bd' }}>
                              {risk.mitigation_steps?.map((step, i) => (
                                <li key={i}>{step}</li>
                              ))}
                            </ul>
                          </Col>
                          <Col md={6}>
                            <h6 className="small text-white mb-2">Related Past Issues</h6>
                            <ul className="small" style={{ color: '#adb5bd' }}>
                              {risk.related_past_issues?.length > 0 ? 
                                risk.related_past_issues.map((issue, i) => (
                                  <li key={i}>{issue}</li>
                                )) : (
                                  <li>No past issues recorded</li>
                                )
                              }
                            </ul>
                          </Col>
                        </Row>
                        
                        <div className="mt-2">
                          <h6 className="small text-white mb-1">Contingency Plan</h6>
                          <p className="small" style={{ color: '#adb5bd' }}>{risk.contingency_plan}</p>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };
  
  // Equipment Health Assessment
  const renderEquipmentHealth = () => {
    return (
      <div className="mt-3">
        <h5 className="text-white mb-3">Equipment Health Assessment</h5>
        <Accordion>
          {data.equipment_health_assessment?.map((system, sysIndex) => (
            <Accordion.Item 
              key={sysIndex} 
              eventKey={sysIndex.toString()}
              className="mb-3"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px'
              }}
            >
              <Accordion.Header style={{ color: '#ffffff' }}>
                <div className="d-flex justify-content-between w-100 align-items-center pe-3">
                  <div>
                    <strong>{system.system_category} System</strong>
                    <span className="ms-2 text-muted">({system.components?.length || 0} components)</span>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body style={{ background: 'rgba(0, 0, 0, 0.2)' }}>
                {system.components?.map((component, compIndex) => (
                  <Card 
                    key={compIndex}
                    className="mb-3" 
                    style={{ 
                      background: 'rgba(255, 255, 255, 0.05)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 style={{ color: '#d9d9d9' }}>{component.name}</h6>
                          <p className="small text-muted mb-1">
                            {component.make_model} | Age: {component.age} years
                          </p>
                        </div>
                        <Badge style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}>
                          {component.maintenance_urgency} Urgency
                        </Badge>
                      </div>
                      
                      <div className="mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">Condition Rating</small>
                          <small className="text-muted">{component.condition_rating}/5</small>
                        </div>
                        <ProgressBar 
                          now={component.condition_rating * 20} 
                          variant={component.condition_rating > 3 ? 'success' : 
                                 component.condition_rating > 2 ? 'info' : 'danger'}
                          style={{ height: '8px' }}
                        />
                      </div>
                      
                      <Row className="mt-3">
                        <Col md={6}>
                          <p className="small mb-1" style={{ color: '#d9d9d9' }}>
                            <strong>Failure Probability:</strong> {(component.failure_probability * 100).toFixed(1)}%
                          </p>
                          <p className="small mb-2" style={{ color: '#d9d9d9' }}>
                            <strong>Remaining Life:</strong> {component.estimated_remaining_life}
                          </p>
                          <p className="small mb-1" style={{ color: '#d9d9d9' }}>
                            <strong>Replacement Cost:</strong> {component.replacement_cost}
                          </p>
                        </Col>
                        <Col md={6}>
                          <h6 className="small text-white mb-1">Warning Signs</h6>
                          {component.warning_signs?.length > 0 ? (
                            <ul className="small mb-0" style={{ color: '#adb5bd' }}>
                              {component.warning_signs.map((sign, i) => (
                                <li key={i}>{sign}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="small text-muted">No warning signs detected</p>
                          )}
                        </Col>
                      </Row>
                      
                      <p className="small mt-2" style={{ color: '#d9d9d9' }}>
                        <strong>Notes:</strong> {component.notes}
                      </p>
                    </Card.Body>
                  </Card>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };
  
  // Long-term Recommendations
  const renderLongTermRecommendations = () => {
    return (
      <div className="mt-3">
        <h5 className="text-white mb-3">Long-Term Recommendations ({data.long_term_recommendations?.length || 0})</h5>
        <Accordion>
          {data.long_term_recommendations?.map((rec, index) => (
            <Accordion.Item 
              key={index} 
              eventKey={index.toString()}
              className="mb-3"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px'
              }}
            >
              <Accordion.Header style={{ color: '#ffffff' }}>
                <div className="d-flex justify-content-between w-100 align-items-center pe-3">
                  <div>
                    <Badge 
                      style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }} 
                      className="me-2"
                    >
                      {rec.priority}
                    </Badge>
                    <strong>{rec.category}</strong> - {rec.recommendation}
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body style={{ background: 'rgba(0, 0, 0, 0.2)' }}>
                <p className="small" style={{ color: '#d9d9d9' }}>{rec.justification}</p>
                
                <Row className="mt-3">
                  <Col md={6}>
                    <p className="small mb-1 text-white">
                      <strong>Estimated Cost:</strong> {rec.estimated_cost}
                    </p>
                    <p className="small mb-1 text-white">
                      <strong>Implementation Timeline:</strong> {rec.implementation_timeline}
                    </p>
                    <p className="small mb-3 text-white">
                      <strong>ROI Estimate:</strong> {rec.roi_estimate}
                    </p>
                    
                    <h6 className="small text-white mb-1">Affected Systems</h6>
                    <ul className="small" style={{ color: '#adb5bd' }}>
                      {rec.affected_systems?.map((sys, i) => (
                        <li key={i}>{sys}</li>
                      ))}
                    </ul>
                  </Col>
                  <Col md={6}>
                    <h6 className="small text-white mb-1">Expected Benefits</h6>
                    <ul className="small" style={{ color: '#adb5bd' }}>
                      {rec.expected_benefits?.map((benefit, i) => (
                        <li key={i}>{benefit}</li>
                      ))}
                    </ul>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </div>
    );
  };

  // Spare Parts Recommendations
  const renderSpareParts = () => {
    return (
      <div className="mt-3">
        <h5 className="text-white mb-3">Spare Parts Recommendations ({data.spare_parts_recommendations?.length || 0})</h5>
        <Table variant="dark" striped responsive className="mt-2" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th>Part Name</th>
              <th>Qty</th>
              <th>Criticality</th>
              <th>Est. Cost</th>
              <th>Lead Time</th>
              <th>Compatible With</th>
            </tr>
          </thead>
          <tbody>
            {data.spare_parts_recommendations?.map((part, index) => (
              <tr key={index}>
                <td>
                  <strong>{part.part_name}</strong>
                  <div className="text-muted small">{part.justification}</div>
                </td>
                <td>{part.quantity_recommended}</td>
                <td>
                  <Badge style={{ background: getCriticalityBadgeColor(part.criticality) }}>
                    {part.criticality}
                  </Badge>
                </td>
                <td>{part.estimated_cost}</td>
                <td>{part.lead_time}</td>
                <td>
                  <ul className="mb-0 ps-3 small">
                    {part.compatible_equipment?.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Staffing Recommendations
  const renderStaffing = () => {
    return (
      <div className="mt-3">
        <h5 className="text-white mb-3">Staffing Recommendations ({data.staffing_recommendations?.length || 0})</h5>
        <Table variant="dark" striped responsive className="mt-2" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th>Event Type</th>
              <th>Role</th>
              <th>Qty</th>
              <th>Expertise</th>
              <th>Coverage</th>
              <th>Cost Est.</th>
            </tr>
          </thead>
          <tbody>
            {data.staffing_recommendations?.map((staff, index) => (
              <tr key={index}>
                <td>{staff.event_type}</td>
                <td>
                  <strong>{staff.role}</strong>
                  <div className="text-muted small">{staff.justification}</div>
                </td>
                <td>{staff.quantity_needed}</td>
                <td>
                  <Badge style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}>
                    {staff.expertise_level}
                  </Badge>
                  <div className="mt-1 small">
                    {staff.specialized_skills?.slice(0, 2).map((skill, i) => (
                      <div key={i} className="text-muted">{skill}</div>
                    ))}
                    {staff.specialized_skills?.length > 2 && 
                      <div className="text-muted">+{staff.specialized_skills.length - 2} more</div>
                    }
                  </div>
                </td>
                <td>{staff.ideal_coverage_hours}</td>
                <td>{staff.cost_estimate}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  // Health Scores
  const renderHealthScores = () => {
    const scores = data.venue_health_scores || {};
    const systemScores = scores.system_scores || {};
    
    return (
      <div className="mt-3">
        <h5 className="text-white mb-3">Venue Health Scores</h5>
        
        <Card style={{ 
          background: 'rgba(255, 255, 255, 0.05)', 
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <Card.Body className="p-4">
            <div className="text-center mb-4">
              <h2 className="text-white mb-0">{scores.overall_score || '—'}</h2>
              <div className="text-muted small">Overall Health Score</div>
            </div>
            
            <ProgressBar 
              now={scores.overall_score} 
              variant={scores.overall_score > 80 ? 'success' : 
                    scores.overall_score > 60 ? 'info' :
                    scores.overall_score > 40 ? 'warning' : 'danger'}
              style={{ height: '15px', marginBottom: '25px' }} 
            />
            
            <Row className="mb-3 text-center g-3">
              <Col xs={4}>
                <Card style={{ background: 'rgba(255, 255, 255, 0.07)', border: 'none' }}>
                  <Card.Body className="p-2">
                    <h4 className="text-white mb-0">{scores.reliability_score || '—'}</h4>
                    <div className="text-muted small">Reliability</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={4}>
                <Card style={{ background: 'rgba(255, 255, 255, 0.07)', border: 'none' }}>
                  <Card.Body className="p-2">
                    <h4 className="text-white mb-0">{scores.safety_score || '—'}</h4>
                    <div className="text-muted small">Safety</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={4}>
                <Card style={{ background: 'rgba(255, 255, 255, 0.07)', border: 'none' }}>
                  <Card.Body className="p-2">
                    <h4 className="text-white mb-0">{scores.readiness_for_upcoming_events || '—'}</h4>
                    <div className="text-muted small">Event Readiness</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <h6 className="text-white mb-3">System Health Scores</h6>
        <Row className="g-3">
          {Object.entries(systemScores).map(([system, score], index) => (
            <Col md={6} key={index}>
              <Card style={{ 
                background: 'rgba(255, 255, 255, 0.05)', 
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Card.Body className="p-2">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="text-white" style={{ textTransform: 'capitalize' }}>{system}</div>
                    <div className="text-muted small">{score}/100</div>
                  </div>
                  {renderScoreProgressBar(score)}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };
  
  // Summary Tab Content
  const renderSummary = () => {
    const summary = data.summary || {};
    const venueSummary = data.venue_summary || {};
    
    return (
      <div className="mt-3">
        <Card style={{ 
          background: 'rgba(255, 255, 255, 0.05)', 
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '8px'
        }}>
          <Card.Body>
            <Row>
              <Col md={6} className="mb-3">
                <h5 className="text-white mb-3">Venue Overview</h5>
                <ListGroup variant="flush" style={{ background: 'transparent' }}>
                  <ListGroup.Item style={{ background: 'rgba(0, 0, 0, 0.2)', color: '#d9d9d9', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <strong>Name:</strong> {venueSummary.name}
                  </ListGroup.Item>
                  <ListGroup.Item style={{ background: 'rgba(0, 0, 0, 0.2)', color: '#d9d9d9', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <strong>Type:</strong> {venueSummary.type}
                  </ListGroup.Item>
                  <ListGroup.Item style={{ background: 'rgba(0, 0, 0, 0.2)', color: '#d9d9d9', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <strong>Age:</strong> {venueSummary.age} years
                  </ListGroup.Item>
                  <ListGroup.Item style={{ background: 'rgba(0, 0, 0, 0.2)', color: '#d9d9d9', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                    <strong>Overall Condition:</strong> {venueSummary.overall_condition}
                  </ListGroup.Item>
                </ListGroup>
              </Col>
              
              <Col md={6} className="mb-3">
                <h5 className="text-white mb-3">Health Scores</h5>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-white">Overall Health</span>
                    <span className="text-muted small">{data.venue_health_scores?.overall_score || 0}/100</span>
                  </div>
                  {renderScoreProgressBar(data.venue_health_scores?.overall_score || 0)}
                </div>
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-white">Reliability</span>
                    <span className="text-muted small">{data.venue_health_scores?.reliability_score || 0}/100</span>
                  </div>
                  {renderScoreProgressBar(data.venue_health_scores?.reliability_score || 0)}
                </div>
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-white">Event Readiness</span>
                    <span className="text-muted small">{data.venue_health_scores?.readiness_for_upcoming_events || 0}/100</span>
                  </div>
                  {renderScoreProgressBar(data.venue_health_scores?.readiness_for_upcoming_events || 0)}
                </div>
              </Col>
            </Row>
            
            <Row className="mt-2">
              <Col md={12}>
                <h5 className="text-white mb-3">Maintenance Summary</h5>
                <Row>
                  <Col xs={6} md={3} className="mb-3">
                    <Card style={{ background: 'rgba(220, 53, 69, 0.15)', border: '1px solid rgba(220, 53, 69, 0.3)' }}>
                      <Card.Body className="p-3 text-center">
                        <h3 className="text-white">{summary.critical_issues_count || 0}</h3>
                        <div className="text-muted small">Critical Issues</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={6} md={3} className="mb-3">
                    <Card style={{ background: 'rgba(255, 193, 7, 0.15)', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
                      <Card.Body className="p-3 text-center">
                        <h3 className="text-white">{summary.high_priority_tasks_count || 0}</h3>
                        <div className="text-muted small">High Priority</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={6} md={3} className="mb-3">
                    <Card style={{ background: 'rgba(23, 162, 184, 0.15)', border: '1px solid rgba(23, 162, 184, 0.3)' }}>
                      <Card.Body className="p-3 text-center">
                        <h3 className="text-white">{summary.medium_priority_tasks_count || 0}</h3>
                        <div className="text-muted small">Medium Priority</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col xs={6} md={3} className="mb-3">
                    <Card style={{ background: 'rgba(40, 167, 69, 0.15)', border: '1px solid rgba(40, 167, 69, 0.3)' }}>
                      <Card.Body className="p-3 text-center">
                        <h3 className="text-white">{summary.low_priority_tasks_count || 0}</h3>
                        <div className="text-muted small">Low Priority</div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                
                <Row className="mt-2">
                  <Col md={6} className="mb-3">
                    <ListGroup variant="flush">
                      <ListGroup.Item style={{ background: 'rgba(0, 0, 0, 0.2)', color: '#d9d9d9', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                        <FontAwesomeIcon icon={faClock} className="me-2 text-warning" />
                        <strong>Total Maintenance Hours:</strong> {summary.estimated_total_maintenance_hours || 0} hours
                      </ListGroup.Item>
                      <ListGroup.Item style={{ background: 'rgba(0, 0, 0, 0.2)', color: '#d9d9d9', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                        <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
                        <strong>Estimated Cost:</strong> {summary.estimated_total_maintenance_cost || '$0'}
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                  
                  <Col md={6} className="mb-3">
                    <h6 className="text-white mb-2">Main Concerns</h6>
                    <ul style={{ color: '#d9d9d9', paddingLeft: '20px', fontSize: '0.85rem' }}>
                      {summary.main_concerns?.map((concern, index) => (
                        <li key={index} className="mb-1">{concern}</li>
                      ))}
                    </ul>
                  </Col>
                </Row>
                
                <Row className="mt-2">
                  <Col md={12}>
                    <h6 className="text-white mb-2">Strengths</h6>
                    <div className="d-flex flex-wrap">
                      {summary.strengths?.map((strength, index) => (
                        <Badge 
                          key={index}
                          style={{ 
                            background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)',
                            padding: '8px 12px', 
                            fontSize: '0.85rem' 
                          }}
                          className="me-2 mb-2" 
                        >
                          <FontAwesomeIcon icon={faCheck} className="me-1" />
                          {strength}
                        </Badge>
                      ))}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    );
  };
  
  return (
    <Container fluid style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <Card className="dark-main-card text-white mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 style={{ color: '#d9d9d9', marginBottom: '5px' }}>
                Predictive Maintenance Plan
                {fromSaved && <Badge bg="info" className="ms-2">Saved Plan</Badge>}
              </h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.9em' }}>
                Generated for {data.venue_summary?.name || 'your venue'} on {new Date().toLocaleDateString()}
              </p>
            </div>
            <div>
              <Button 
                variant="outline-light" 
                size="sm" 
                className="me-2"
                style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.15)' }}
                onClick={handlePrintPlan}
              >
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Print
              </Button>
              {fromSaved ? (
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={onBack}
                >
                  Back to Saved Plans
                </Button>
              ) : (
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={onReset}
                >
                  <FontAwesomeIcon icon={faRedo} className="me-2" />
                  New Plan
                </Button>
              )}
            </div>
          </div>
          
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Nav variant="pills" className="mb-4">
              <Nav.Item>
                <Nav.Link 
                  eventKey="summary"
                  className={activeTab === 'summary' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'summary' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faChartBar} className="me-1" />
                  Summary
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="tasks" 
                  className={activeTab === 'tasks' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'tasks' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                  Maintenance Tasks
                  {data.maintenance_tasks?.length > 0 && (
                    <Badge bg="danger" pill className="ms-2" style={{ fontSize: '0.7rem' }}>
                      {data.maintenance_tasks.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="risks" 
                  className={activeTab === 'risks' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'risks' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  Event Risks
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="equipment" 
                  className={activeTab === 'equipment' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'equipment' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faTools} className="me-1" />
                  Equipment Health
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="longterm" 
                  className={activeTab === 'longterm' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'longterm' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} className="me-1" />
                  Long-Term
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="parts" 
                  className={activeTab === 'parts' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'parts' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faShoppingCart} className="me-1" />
                  Spare Parts
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="staffing" 
                  className={activeTab === 'staffing' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'staffing' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faUsers} className="me-1" />
                  Staffing
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link 
                  eventKey="scores" 
                  className={activeTab === 'scores' ? 'active' : ''}
                  style={{ 
                    background: activeTab === 'scores' ? 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' : 'rgba(255, 255, 255, 0.1)', 
                    color: '#ffffff',
                    border: 'none',
                    margin: '0 5px 5px 0'
                  }}
                >
                  <FontAwesomeIcon icon={faHeartbeat} className="me-1" />
                  Health Scores
                </Nav.Link>
              </Nav.Item>
            </Nav>
            
            <Tab.Content>
              <Tab.Pane eventKey="summary">
                {renderSummary()}
              </Tab.Pane>
              <Tab.Pane eventKey="tasks">
                {renderMaintenanceTasks()}
              </Tab.Pane>
              <Tab.Pane eventKey="risks">
                {renderRiskAssessment()}
              </Tab.Pane>
              <Tab.Pane eventKey="equipment">
                {renderEquipmentHealth()}
              </Tab.Pane>
              <Tab.Pane eventKey="longterm">
                {renderLongTermRecommendations()}
              </Tab.Pane>
              <Tab.Pane eventKey="parts">
                {renderSpareParts()}
              </Tab.Pane>
              <Tab.Pane eventKey="staffing">
                {renderStaffing()}
              </Tab.Pane>
              <Tab.Pane eventKey="scores">
                {renderHealthScores()}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Results;